import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5 }}
    className="glass-card p-6 md:p-8 mb-6"
  >
    <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
    <div className="space-y-4 text-gray-300 leading-relaxed">{children}</div>
  </motion.div>
);

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-100 via-surface-200 to-surface-100">
      {/* Hero Section */}
      <section className="pt-24 pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="relative">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Privacy
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-neon-400">
                  {' '}
                  Policy
                </span>
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary-400 to-neon-400 rounded-full mx-auto mb-6"></div>
            </div>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              How Documint Wallet handles your information.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Effective Date: November 8, 2025 &middot; Last Updated: August 19,
              2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Section title="Introduction">
              <p>
                Documint (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;)
                operates the Documint Wallet mobile application (the
                &quot;App&quot;). This Privacy Policy explains how we handle
                information when you use our App.
              </p>
              <p>
                <strong className="text-white">Our Commitment</strong>: Documint
                Wallet is built on the principle of self-sovereign identity.
                Your verifiable credentials and their content, and your
                cryptographic keys, are stored on your device and are never
                transmitted to our servers. Some limited operational data —
                described below — is sent to our backend to make features like
                push notifications and credential signing work.
              </p>
            </Section>

            <Section title="Information We Collect">
              <h3 className="text-lg font-semibold text-white">
                Sent to Our Servers (api.documint.id)
              </h3>
              <p>
                <strong className="text-white">
                  Device and push notification data
                </strong>
                : To deliver push notifications, the app registers your
                device&apos;s push token, platform (iOS/Android), device model,
                device name, OS version, and app version with our backend. This
                is refreshed periodically (roughly every 7 days) or when it
                changes.
              </p>
              <p>
                <strong className="text-white">
                  Decentralized Identifier (DID)
                </strong>
                : Your DID (and an optional alias) is registered with our
                backend so the wallet can be linked to your device registration
                and so credential-signing requests can be routed to the correct
                wallet. We do not store your private keys — those remain on your
                device.
              </p>
              <p>
                <strong className="text-white">
                  Authentication session data
                </strong>
                : The app authenticates with our backend using a DID-based
                challenge-response flow (not a traditional username/password).
                Access and refresh tokens issued by this flow are stored
                securely on your device.
              </p>
              <p>
                <strong className="text-white">
                  Credential signing requests
                </strong>
                : When you request a credential to be signed, the credential
                type, subject, and your holder DID are sent to our backend as
                part of that signing request.
              </p>
              <p>
                <strong className="text-white">
                  Aadhaar (Indian government ID) import
                </strong>
                : If you choose to import an Aadhaar-based credential, the UIDAI
                app on your device delivers a signed data package to our
                backend, and the wallet retrieves it via a session-scoped
                request. This data transits our backend as a relay to get it
                onto your device; see &quot;Data Retention&quot; below for how
                long it&apos;s kept there.
              </p>
              <p>
                <strong className="text-white">
                  Location (optional, credential-scoped)
                </strong>
                : If you create a location-based verifiable credential, the
                location you captured becomes part of that credential&apos;s
                content, which may be sent to a verifier when you choose to
                present it. We do not collect location in the background or
                independently of a credential you create.
              </p>

              <h3 className="text-lg font-semibold text-white pt-2">
                Not Collected
              </h3>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  Camera images or QR code contents (processed locally; only
                  decoded and used, not transmitted or stored by us)
                </li>
                <li>
                  Biometric data (Face ID/Touch ID data never leaves the OS and
                  is not accessible to the app or to us)
                </li>
                <li>Usage analytics, telemetry, or advertising identifiers</li>
                <li>
                  Credential content you haven&apos;t explicitly signed, shared,
                  or synced through the flows above
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white pt-2">
                Stored Only On Your Device
              </h3>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  Verifiable credentials (encrypted, iOS Keychain / Android
                  Keystore)
                </li>
                <li>DIDs and their associated private/cryptographic keys</li>
                <li>
                  PIN (as a cryptographic hash) and biometric authentication
                  settings
                </li>
                <li>App settings and preferences</li>
              </ul>
              <p>
                <strong className="text-white">Important</strong>: Because
                credential content and keys are stored only on your device, if
                you uninstall the app or lose your device without a backup, your
                credentials and identities will be permanently lost. We cannot
                recover this data.
              </p>
            </Section>

            <Section title="Permissions">
              <h3 className="text-lg font-semibold text-white">
                Camera Permission
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Purpose</strong>: To scan QR
                  codes for receiving credentials and verifiable presentation
                  requests
                </li>
                <li>
                  <strong className="text-white">Usage</strong>: Camera is only
                  active when you explicitly open the QR scanner
                </li>
                <li>
                  <strong className="text-white">Data Storage</strong>: QR code
                  data is processed locally; no images or camera data are stored
                  or transmitted by us
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white pt-2">
                Biometric Authentication (Face ID, Touch ID, Fingerprint)
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Purpose</strong>: To
                  authenticate and unlock your wallet
                </li>
                <li>
                  <strong className="text-white">Usage</strong>: Biometric data
                  is processed entirely by your device&apos;s operating system
                </li>
                <li>
                  <strong className="text-white">Data Storage</strong>:
                  Biometric data never leaves your device and is not accessible
                  to the App or to us
                </li>
                <li>
                  <strong className="text-white">Optional</strong>: You can
                  choose to use PIN-only authentication
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white pt-2">
                Location (Optional)
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Purpose</strong>: To create
                  location-based credentials if you choose
                </li>
                <li>
                  <strong className="text-white">Usage</strong>: Only accessed
                  when you explicitly create a credential that includes location
                  data
                </li>
                <li>
                  <strong className="text-white">Data Storage</strong>: Location
                  data becomes part of the credential you create; see
                  &quot;Information We Collect&quot; above for when it may be
                  transmitted
                </li>
                <li>
                  <strong className="text-white">Optional</strong>: This
                  permission is entirely optional and not required for core app
                  functionality
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white pt-2">
                Internet Access
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Purpose</strong>: To
                  communicate with our backend (device/push registration, DID
                  registration, authentication, credential signing) and directly
                  with credential issuers and verifiers when presenting or
                  receiving credentials
                </li>
                <li>
                  <strong className="text-white">Data Transmitted</strong>: See
                  &quot;Information We Collect&quot; above, plus credential
                  presentation proofs and issuance requests as required by the
                  OpenID4VCI/OpenID4VP protocols
                </li>
                <li>
                  <strong className="text-white">Note</strong>: Credential
                  viewing works offline once credentials are stored; network
                  access is needed for registration, signing, and credential
                  exchange
                </li>
              </ul>
            </Section>

            <Section title="Third-Party Services">
              <p>
                <strong className="text-white">
                  Credential issuers and verifiers
                </strong>
                : When you interact with credential issuers (to receive
                credentials) or verifiers (to present credentials), you are
                communicating directly with those third parties over the
                OpenID4VCI/OpenID4VP protocols. Each issuer and verifier has
                their own privacy policy. We recommend reviewing their privacy
                practices before sharing information. We do not intercept or
                store the content of these exchanges.
              </p>
              <p>
                <strong className="text-white">UIDAI (Aadhaar)</strong>: If you
                choose to import an Aadhaar credential, you interact with the
                UIDAI app/service directly, which is governed by UIDAI&apos;s
                own privacy terms.
              </p>
              <p>
                <strong className="text-white">
                  Crash reporting (currently inactive)
                </strong>
                : The app currently bundles the Sentry SDK as a dependency, but
                it is not initialized or configured in the current build — no
                crash reports, breadcrumbs, or diagnostic data are sent to
                Sentry or any third party today. If we activate crash reporting
                in a future release, we will update this policy first and
                disclose what is collected.
              </p>
              <p>
                <strong className="text-white">
                  No analytics or advertising
                </strong>
                : We do not use Google Analytics, Firebase Analytics, Amplitude,
                Mixpanel, or any advertising network.
              </p>
            </Section>

            <Section title="Data Security">
              <p>
                <strong className="text-white">Encryption</strong>: Credentials
                and private keys are encrypted using industry-standard
                encryption and stored in your device&apos;s secure storage. Data
                in transit to our backend uses HTTPS/TLS.
              </p>
              <p>
                <strong className="text-white">Authentication</strong>: Access
                to your wallet requires authentication (PIN and/or biometric)
                that you control.
              </p>
              <p>
                <strong className="text-white">
                  Limited server-side footprint
                </strong>
                : Our backend stores only what&apos;s needed to operate
                device/push registration, DID-to-device linking, authentication
                sessions, and (transiently) Aadhaar relay data — never your
                credential content or private keys.
              </p>
              <p>
                <strong className="text-white">No Backdoors</strong>: We do not
                have any mechanism to access your credential content or decrypt
                your locally-stored data.
              </p>
            </Section>

            <Section title="Data Sharing">
              <p>
                We do not sell your data or share it with third parties for
                advertising or marketing purposes.
              </p>
              <p>When you present credentials to verifiers:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>You explicitly choose which credentials to share</li>
                <li>You control when and with whom to share</li>
                <li>
                  The presentation is transmitted directly from your device to
                  the verifier per the OpenID4VP protocol
                </li>
              </ul>
            </Section>

            <Section title="Data Retention">
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <strong className="text-white">
                    Device/push registration and DID registration data
                  </strong>
                  : retained on our backend while your device/wallet remains
                  registered, and refreshed periodically as described above.
                </li>
                <li>
                  <strong className="text-white">
                    Authentication session tokens
                  </strong>
                  : standard session/refresh-token lifetimes; stored on your
                  device, not persisted server-side beyond what&apos;s needed to
                  validate sessions.
                </li>
                <li>
                  <strong className="text-white">Aadhaar relay data</strong>:
                  retained only transiently to allow retrieval by your device;
                  not retained long-term as credential content.
                </li>
              </ul>
              <p>
                You can request deletion of your server-side device/DID
                registration data by contacting us (see below).
              </p>
            </Section>

            <Section title="Children's Privacy">
              <p>
                The app is rated 4+ and does not knowingly collect data from
                children. Parents and guardians should supervise children&apos;s
                use of credential management applications.
              </p>
            </Section>

            <Section title="Your Rights">
              <h3 className="text-lg font-semibold text-white">Data Access</h3>
              <p>
                Credential content, DIDs, and keys are stored on your device and
                viewable within the app at any time. To request a copy of the
                limited server-side data described above (device registration,
                DID linkage), contact us.
              </p>

              <h3 className="text-lg font-semibold text-white pt-2">
                Data Deletion
              </h3>
              <p>
                You can delete individual credentials or your entire wallet at
                any time through the app settings. Uninstalling the app
                permanently deletes all local data (this cannot be undone). To
                request deletion of server-side device/DID registration data,
                contact us.
              </p>

              <h3 className="text-lg font-semibold text-white pt-2">
                Data Portability
              </h3>
              <p>
                Your credentials are stored in standard W3C Verifiable
                Credentials format.
              </p>
            </Section>

            <Section title="GDPR Compliance (European Users)">
              <p>
                For users in the European Economic Area (EEA), United Kingdom,
                and Switzerland: we process the limited operational data
                described above (device/push identifiers, DID, authentication
                tokens, and transient Aadhaar relay data where applicable) as
                necessary to provide the app&apos;s core functionality. You may
                exercise your GDPR rights (access, rectification, erasure,
                portability) by contacting us using the details below.
              </p>
            </Section>

            <Section title="CCPA Compliance (California Users)">
              <p>
                <strong className="text-white">
                  Categories of Personal Information Collected
                </strong>
                : Device/push identifiers, decentralized identifiers,
                authentication session data, and (where applicable)
                government-ID-related data transiting as part of the Aadhaar
                import flow.
              </p>
              <p>
                <strong className="text-white">
                  Sale of Personal Information
                </strong>
                : We do not sell personal information.
              </p>
              <p>
                <strong className="text-white">CCPA Rights</strong>: You may
                exercise your right to know, delete, or opt out of any
                applicable data practices by contacting us using the details
                below.
              </p>
            </Section>

            <Section title="Changes to This Privacy Policy">
              <p>
                We may update this Privacy Policy from time to time. We will
                notify you of any changes by:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  Updating the &quot;Last Updated&quot; date at the top of this
                  policy
                </li>
                <li>Displaying a notice in the app (for material changes)</li>
                <li>
                  Posting the updated policy at https://documint.id/privacy
                </li>
              </ul>
              <p>
                Your continued use of the app after changes constitutes
                acceptance of the updated policy.
              </p>
            </Section>

            <Section title="Contact Us">
              <p>
                If you have questions about this Privacy Policy or our privacy
                practices, please contact us:
              </p>
              <p>
                <strong className="text-white">Email</strong>:
                privacy@documint.id
                <br />
                <strong className="text-white">Website</strong>:
                https://documint.id
                <br />
                <strong className="text-white">Support</strong>:
                support@documint.id
              </p>
              <p>
                <strong className="text-white">Mailing Address</strong>:<br />
                Documint Team
                <br />
                Kodiswaran Babu Janardhanan
                <br />
                Divyasree Elan, Sarjapur Main Road
                <br />
                1F 801
                <br />
                Bangalore - 560035
                <br />
                India
              </p>
            </Section>

            <Section title="Technical Details for Privacy Researchers">
              <h3 className="text-lg font-semibold text-white">Data Storage</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Platform</strong>: iOS Keychain
                  (iOS), Android Keystore (Android) for credential content and
                  keys
                </li>
                <li>
                  <strong className="text-white">Encryption</strong>:
                  Industry-standard encryption for credential data
                </li>
                <li>
                  <strong className="text-white">Biometric</strong>: Native
                  platform biometric APIs (no biometric data accessible to app
                  or backend)
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white pt-2">
                Network Communication
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Backend</strong>:
                  api.documint.id — device/push registration, DID registration,
                  authentication, credential signing, Aadhaar relay
                </li>
                <li>
                  <strong className="text-white">Protocols</strong>: OpenID4VCI,
                  OpenID4VP (W3C standards) for direct issuer/verifier exchange
                </li>
                <li>
                  <strong className="text-white">TLS</strong>: All external
                  communication over HTTPS/TLS
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white pt-2">Logging</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-white">Debug Logs</strong>: Enabled in
                  development builds only
                </li>
                <li>
                  <strong className="text-white">Crash Reporting</strong>:
                  Sentry SDK is bundled but not currently initialized; no crash
                  data is transmitted today
                </li>
              </ul>
            </Section>

            <p className="text-sm text-gray-400 text-center mt-8">
              This privacy policy is provided for informational purposes. For
              legal advice regarding privacy compliance in your jurisdiction,
              consult a qualified attorney. Documint makes no warranties
              regarding the legal sufficiency of this policy.
            </p>
            <p className="text-sm text-gray-500 text-center mt-4">
              &copy; 2026 Documint. All rights reserved.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
